<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

export default {
  components: { LoadingState },
  props: {
    inbox: { type: Object, default: () => ({}) },
  },
  data() {
    return { selectedAgentBotId: null };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](this.inbox.id);
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
  },
  methods: {
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (_) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="bc-page">
    <div class="bc-body">

      <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />

      <template v-else>
        <!-- ── Header ── -->
        <div class="bc-header">
          <h2 class="bc-title">
            {{ $t('AGENT_BOTS.BOT_CONFIGURATION.TITLE') }}
          </h2>
          <p class="bc-desc">
            {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DESC') }}
          </p>
        </div>

        <!-- ── Bot selector card ── -->
        <section class="bc-section">
          <h3 class="bc-section__title">
            {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER') }}
          </h3>

          <div class="bc-card">
            <!-- empty state -->
            <div v-if="!agentBots.length" class="bc-empty">
              <span class="bc-empty__icon">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </span>
              <p class="bc-empty__text">No agent bots configured yet.</p>
            </div>

            <template v-else>
              <div class="bc-field">
                <label class="bc-label" for="bc-bot-select">
                  Select bot
                </label>
                <select
                  id="bc-bot-select"
                  v-model="selectedAgentBotId"
                  class="bc-select"
                >
                  <option value="" disabled selected>
                    {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER') }}
                  </option>
                  <option
                    v-for="bot in agentBots"
                    :key="bot.id"
                    :value="bot.id"
                  >
                    {{ bot.name }}
                  </option>
                </select>
              </div>

              <!-- Active bot indicator -->
              <div v-if="activeAgentBot && activeAgentBot.id" class="bc-active-row">
                <span class="bc-active-dot" />
                <span class="bc-active-text">
                  Currently connected: <strong>{{ activeAgentBot.name }}</strong>
                </span>
              </div>
            </template>
          </div>
        </section>

        <!-- ── Actions ── -->
        <div class="bc-footer">
          <button
            class="bc-btn bc-btn--primary"
            type="button"
            :disabled="uiFlags.isSettingAgentBot"
            @click="updateActiveAgentBot"
          >
            <span v-if="uiFlags.isSettingAgentBot" class="bc-spinner" />
            {{ uiFlags.isSettingAgentBot ? '…' : $t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT') }}
          </button>
          <button
            class="bc-btn bc-btn--danger"
            type="button"
            :disabled="!selectedAgentBotId || uiFlags.isDisconnecting"
            @click="disconnectBot"
          >
            <span v-if="uiFlags.isDisconnecting" class="bc-spinner bc-spinner--dark" />
            {{ uiFlags.isDisconnecting ? '…' : $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
          </button>
        </div>
      </template>

    </div>
  </div>
</template>

<style scoped>
/* ── Page shell ── */
.bc-page {
  height: 100%;
  overflow-y: auto;
  background: #ffffff;
}

.bc-body {
  max-width: 720px;
  margin: 0;
  padding: 32px 32px 80px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

/* ── Header ── */
.bc-header {}

.bc-title {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}

.bc-desc {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  margin: 0;
  line-height: 1.5;
}

/* ── Section ── */
.bc-section {}

.bc-section__title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 14px;
}

/* ── Card ── */
.bc-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}

/* ── Field ── */
.bc-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.bc-label {
  font-size: 12px;
  font-weight: 600;
  color: #111827;
}

.bc-select {
  width: 100%;
  max-width: 400px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #111827;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;
  cursor: pointer;
  transition: border-color 0.15s;
}

.bc-select:focus {
  border-color: #4384f5;
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.12);
}

/* ── Active row ── */
.bc-active-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.bc-active-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
}

.bc-active-text {
  font-size: 12px;
  color: #166534;
}

/* ── Empty state ── */
.bc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px 0;
  text-align: center;
}

.bc-empty__icon {
  color: #d1d5db;
}

.bc-empty__text {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

/* ── Footer / actions ── */
.bc-footer {
  display: flex;
  gap: 10px;
  align-items: center;
  padding-top: 4px;
  border-top: 1px solid #e5e7eb;
}

.bc-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition:
    background 0.15s,
    border-color 0.15s,
    opacity 0.15s;
}

.bc-btn--primary {
  background: #111827;
  color: #ffffff;
  border-color: #111827;
}

.bc-btn--primary:hover:not(:disabled) {
  background: #1f2937;
}

.bc-btn--danger {
  background: #fff;
  color: #dc2626;
  border-color: #fecaca;
}

.bc-btn--danger:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #fca5a5;
}

.bc-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ── Spinner ── */
.bc-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: bc-spin 0.7s linear infinite;
  flex-shrink: 0;
}

.bc-spinner--dark {
  border-color: rgba(220, 38, 38, 0.25);
  border-top-color: #dc2626;
}

@keyframes bc-spin {
  to { transform: rotate(360deg); }
}
</style>
